import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ComposableMap,
  ZoomableGroup,
  ProjectionConfig,
} from "react-simple-maps";
import { useMediaQuery } from "@mui/material";

import GeoMapContainer from "./GeoMapContainer";
import { Markers } from "./Pins/Markers";
import { AppDispatch, RootState } from "../../../../store/store";
import { setPinsForSelectedCountry } from "../../../../features/map/pinsSlice";
import theme from "../../../../components/styles/theme/theme";
import calculateZoomAndCenter from "./calculateZoomAndCenter";
import { setCenter, setZoom } from "../../../../features/map/mapSlice";

const Map: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    map: { center, selectedCountry },
    pins: { data: dataPins, pinsForSelectedCountry },
  } = useSelector((state: RootState) => state);
  const isXlScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isOverThen1450Resolution = useMediaQuery(theme.breakpoints.up(1450));
  const isOverThen1700Resolution = useMediaQuery(theme.breakpoints.up(1700));

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const handleResize = () => {
    const containerElement = document.querySelector(".ComposableMap-container");
    if (containerElement) {
      const { width, height } = containerElement.getBoundingClientRect();
      setContainerWidth(width);
      setContainerHeight(height);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  let zoom = useSelector((state: RootState) => state.map.zoom);

  useEffect(() => {
    const pinsForRegion =
      dataPins &&
      selectedCountry &&
      dataPins.find((pins) => pins.name.toUpperCase() === selectedCountry.country_name.toUpperCase());
    pinsForRegion && dispatch(setPinsForSelectedCountry(pinsForRegion.data));
  }, [dataPins, dispatch, selectedCountry]);

  useEffect(() => {
    if (selectedCountry) {
      const updatedZoomAndCenter = calculateZoomAndCenter(
        isXlScreen,
        isOverThen1450Resolution,
        isOverThen1700Resolution,
        containerWidth,
        containerHeight,
        zoom,
        selectedCountry
      );

      dispatch(setCenter(updatedZoomAndCenter.center));
      dispatch(setZoom(updatedZoomAndCenter.zoom));
    }
  }, [
    selectedCountry,
    isXlScreen,
    isOverThen1450Resolution,
    isOverThen1700Resolution,
    containerWidth,
    containerHeight,
    zoom,
    dispatch,
  ]);

  const projectionConfig: ProjectionConfig = {
    scale: 300,
  };

  const renderMap = () => {
    return (
      <ComposableMap
        projection="geoMercator"
        projectionConfig={projectionConfig}
        style={{ width: "100%", height: "100%" }}
        className="ComposableMap-container"
      >
        <ZoomableGroup center={center} zoom={zoom} minZoom={zoom}>
          <GeoMapContainer />
          {pinsForSelectedCountry && selectedCountry && (
            <Markers data={pinsForSelectedCountry} />
          )}
        </ZoomableGroup>
      </ComposableMap>
    );
  };

  return (
    <div id="map-container"
      style={{
        paddingLeft: `${isXlScreen ? "98px" : ""}`,
        width: "100%",
        height: "100%",
        maxHeight: "500px",
      }}
    >
      {renderMap()}
    </div>
  );
};

export default Map;
